import { useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import { sendFEEvent } from '../../../eventUtil';
import { usePaymentState } from './use-payment-state';
import { useAPI } from '../../../services/api.service';
import { useCheckout } from '../../../hooks/use-checkout';
import { ECheckoutStatus } from '../../../constants/enums';
import { useAppConfig } from '../../../hooks/use-app-config';
import {
  EChargeStates,
  ESdkEvents,
  EPaymentStates,
  OrderResponse,
  EventParams,
} from '../../../contracts';
import { useCheckoutFlowType } from './use-checkout-flow-type';

const FETCH_INTERVAL_TIME = 2500;
const AXIOS_NETWORK_ERR_CODE = 'ERR_NETWORK';
const MINUTE_IN_MILLISECONDS = 60000;

export function useOrderPolling(handleErrorCallback?: () => void) {
  const { API } = useAPI();
  const interval = useRef<NodeJS.Timeout>();
  const connectionLostTimestamp = useRef<number>(0);
  const { getEventParams } = useCheckout();
  const { featureFlags } = useAppConfig();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { showBoundary } = useErrorBoundary();
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const setCheckoutStatus = usePaymentState((state) => state.setCheckoutStatus);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setPublisherPurchaseId = usePaymentState((state) => state.setPublisherPurchaseId);
  const { isPaymentLinksFlow } = useCheckoutFlowType();
  const isProcessingScreenDisabled = featureFlags?.checkout_disable_processing_screen;
  const isSuccessScreenDisabled = featureFlags?.checkout_disable_success_screen;

  const startOrderPolling = (orderId: string, FEeventParams: Partial<EventParams> = {}) => {
    connectionLostTimestamp.current = Date.now();
    if (interval.current) {
      clearInterval(interval.current);
    }
    if (!isProcessingScreenDisabled) {
      setCheckoutStatus(ECheckoutStatus.PENDING);
    }
    interval.current = setInterval(fetchOrderData, FETCH_INTERVAL_TIME, orderId, FEeventParams);
  };

  async function fetchOrderData(orderId: string, FEeventParams?: Partial<EventParams>) {
    try {
      const orderResponse = await API.getOrder(orderId);
      handleOrderResponse(orderResponse, FEeventParams);
    } catch (error: any) {
      if (error.code === AXIOS_NETWORK_ERR_CODE) {
        handleConnectionLost(FEeventParams);
      } else {
        clearInterval(interval.current);
        sendFEEvent(ESdkEvents.ORDER_COMPLETED_FAILED, {
          ...getEventParams(),
          ...FEeventParams,
          error: {
            isFatal: true,
          },
        });
        showBoundary(t('somethingWentWrong'));
      }
    }
  }

  function handleOrderResponse(order: OrderResponse, FEeventParams?: Partial<EventParams>) {
    if (order?.state === EChargeStates.SUCCESS) {
      setPublisherPurchaseId(order?.publisherPurchaseId);
      sendFEEvent(ESdkEvents.ORDER_COMPLETED_SUCCESS, {
        ...getEventParams(),
        ...FEeventParams,
      });
      if (!isProcessingScreenDisabled) {
        const nextCheckoutStatus =
          isSuccessScreenDisabled && !isPaymentLinksFlow
            ? ECheckoutStatus.SUCCESS_SHOW_PENDING
            : ECheckoutStatus.SUCCESS;
        setCheckoutStatus(nextCheckoutStatus);
      }
      clearInterval(interval.current);
    } else if (order?.state === EChargeStates.FAILED) {
      sendFEEvent(ESdkEvents.ORDER_COMPLETED_FAILED, {
        ...FEeventParams,
        reason: order?.publisherErrorMessage,
      });
      const defaultErrorMessage = t('yourPaymentWasDeclined');
      setErrorMessage(order?.publisherErrorMessage || defaultErrorMessage);
      setCheckoutStatus(ECheckoutStatus.ERROR);
      clearInterval(interval.current);
    } else if (order.state === EPaymentStates.FAILED) {
      // case of payment intent failed in paypal
      const defaultErrorMessage = t('yourPaymentWasDeclined');
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: order?.publisherErrorMessage || defaultErrorMessage,
      });
      handleErrorCallback?.();
      setErrorMessage(order?.publisherErrorMessage || defaultErrorMessage);
      setIsPaymentLoading(false);
      setCheckoutStatus(ECheckoutStatus.INIT);
      clearInterval(interval.current);
    }
  }

  function handleConnectionLost(FEeventParams?: Partial<EventParams>) {
    if (Date.now() - connectionLostTimestamp.current >= MINUTE_IN_MILLISECONDS) {
      clearInterval(interval.current);
      sendFEEvent(ESdkEvents.ORDER_COMPLETED_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: 'Connection lost',
        error: {
          isFatal: true,
          reason: 'Connection lost',
        },
      });
      showBoundary(t('checkTheGameForYourItems'));
    }
  }

  return { startOrderPolling };
}
